'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Lipstick and Lip Balm: What You Swallow Every Day"
      author="MicroPlastics Editorial Team"
      date="April 7, 2025"
      readTime="9 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="Microplastics in lipstick and lip balm, what you swallow"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        The average woman who wears lipstick daily ingests an estimated
        <strong> 24 mg per day, or 8.7 g per year</strong>, over a lifetime
        of lipstick wearing, that adds up to several pounds of cosmetics
        swallowed. Many conventional lipsticks contain <strong>polyethylene,
        polyethylene terephthalate (PET), and acrylates copolymer</strong>
        as binders and film-formers. Lip balms are similar. The Beat the
        Microbead project found microplastics in dozens of mainstream
        lipstick brands. Safer alternatives: <strong>Ilia, RMS Beauty, Kosas,
        Axiology, Burt&apos;s Bees (some lines), Hurraw (lip balm)</strong>.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Average daily ingestion of lipstick: ~24 mg/day, ~8.7 g/year, several pounds over a lifetime of daily wear.</>,
          <>Microplastic-relevant ingredients to scan for: <strong>polyethylene, PET, acrylates copolymer, polyquaternium, nylon-12, carbomer, PEG-(any number)</strong>.</>,
          <>Beat the Microbead project found microplastics in major lipstick brands worldwide.</>,
          <>Lip-skin absorbs more easily than skin elsewhere, chemical migration is meaningful.</>,
          <>Cleanest brands: Ilia, RMS Beauty, Kosas, Axiology, Lily Lolo, Burt&apos;s Bees Natural lines, Hurraw lip balms.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why lipstick ingestion matters
      </h2>
      <p>
        Of all cosmetic products, <strong>lipstick is the most directly
        ingested</strong>. Studies estimate the average daily wearer ingests
        roughly 24 mg of lipstick per day through eating, drinking, and
        licking lips. Over a year, that&apos;s nearly 9 grams. Over a lifetime
        of daily wear, that&apos;s several pounds of cosmetic product
        swallowed. Whatever ingredients are in your lipstick are effectively
        in your diet.
      </p>
      <p>
        Lip skin is also thinner and more permeable than skin elsewhere on the
        body, meaning chemical absorption is meaningfully higher per gram of
        product contact.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The plastic ingredients to scan for
      </h2>

      <BlogComparisonTable
        caption="Microplastic-relevant ingredients in lipstick and lip balm"
        columns={[
          { key: 'ingredient', label: 'Ingredient name' },
          { key: 'function', label: 'Function in product' },
          { key: 'concern', label: 'Concern' },
        ]}
        rows={[
          { ingredient: 'Polyethylene', function: 'Texture, film former', concern: 'Direct microplastic; common in matte lipsticks' },
          { ingredient: 'Polyethylene terephthalate (PET)', function: 'Glitter, shimmer', concern: 'Direct microplastic; metallic / glossy formulas' },
          { ingredient: 'Acrylates copolymer / crosspolymer', function: 'Long-wear film former', concern: 'Synthetic polymer; common in "long-lasting" lipsticks' },
          { ingredient: 'Nylon-12', function: 'Texture, slip', concern: 'Direct microplastic' },
          { ingredient: 'Carbomer', function: 'Thickener', concern: 'Cross-linked polyacrylic acid' },
          { ingredient: 'Polyquaternium-(any number)', function: 'Conditioning agent', concern: 'Synthetic polymer family' },
          { ingredient: 'PEG (any number)', function: 'Humectant, surfactant', concern: 'Petroleum-derived polymer; may have 1,4-dioxane contamination' },
          { ingredient: 'Microcrystalline wax', function: 'Texture', concern: 'Petroleum-derived; not a polymer but petrochemical origin' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Other concerning lipstick ingredients
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Heavy metals</strong>. FDA testing has found lead, cadmium, aluminium in some lipsticks (not added intentionally but present as contaminants).</li>
        <li><strong>Parabens</strong> (methylparaben, propylparaben), endocrine disruptors.</li>
        <li><strong>Synthetic fragrance</strong>, often a mixture of phthalates and PEG-derivatives not disclosed individually.</li>
        <li><strong>FD&amp;C Red 27, Red 30</strong>, petroleum-derived synthetic dyes.</li>
        <li><strong>Mineral oil / paraffinum liquidum</strong>, petroleum derivative; depending on grade, may contain MOSH/MOAH contaminants.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Recommended polymer-free lipstick brands
      </h2>

      <BlogComparisonTable
        caption="Lipstick brands with microplastic-conscious formulations"
        columns={[
          { key: 'brand', label: 'Brand' },
          { key: 'standout', label: 'Standout product' },
          { key: 'cert', label: 'Certifications' },
          { key: 'price', label: 'Price' },
        ]}
        rows={[
          { brand: 'Ilia Beauty', standout: 'Color Block Lipstick', cert: 'EWG VERIFIED some products', price: '$32-36' },
          { brand: 'RMS Beauty', standout: 'Wild with Desire Lipstick', cert: 'EWG VERIFIED', price: '$28-32' },
          { brand: 'Kosas', standout: 'Weightless Lip Color', cert: 'Vegan, cruelty-free, polymer-free', price: '$28-32' },
          { brand: 'Axiology', standout: 'Multi-Use Lip + Cheek (Balmies)', cert: 'Vegan, plastic-free packaging', price: '$15-20' },
          { brand: 'Lily Lolo', standout: 'Natural Lipsticks', cert: 'EWG VERIFIED, cruelty-free', price: '$22' },
          { brand: 'Burt\'s Bees Lipstick (Natural line)', standout: 'Natural Lipsticks (verify ingredient list)', cert: 'Some products certified natural', price: '$10' },
          { brand: 'Hurraw Lip Balm', standout: 'Original / Vanilla', cert: 'Organic, vegan, raw', price: '$5' },
          { brand: 'Eco Lips', standout: 'Lip Balm', cert: 'USDA Organic', price: '$3-5' },
          { brand: 'Conventional drugstore lipstick (Maybelline, Revlon, Covergirl)', standout: 'Various', cert: 'Generally polymer-containing', price: '$5-15' },
          { brand: 'Long-wear / matte / "24-hour" lipstick (most brands)', standout: 'Various', cert: 'Polymer-heavy for staying power', price: '$10-25' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How to read a lipstick label
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Scan the ingredient list</strong> for polyethylene, PET, acrylates copolymer, polyquaternium, nylon-12, carbomer, and PEG.</li>
        <li><strong>Avoid &ldquo;long-wear&rdquo;, &ldquo;24-hour&rdquo;, &ldquo;transfer-proof&rdquo; products</strong>, these rely heavily on synthetic polymer film-formers.</li>
        <li><strong>Avoid glittery / metallic / shimmer products</strong>, typically contain PET glitter.</li>
        <li><strong>Look for &ldquo;fragrance-free&rdquo;</strong>, &ldquo;fragrance&rdquo; often hides phthalates.</li>
        <li><strong>Look for third-party certifications:</strong> EWG VERIFIED, MADE SAFE, COSMOS Organic, USDA Organic.</li>
        <li><strong>Beat the Microbead app</strong>, scan products for microplastic ingredients in their database.</li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-cosmetics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in cosmetics
        </Link>,{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in toothpaste
        </Link>, and{' '}
        <Link href="/blog/worst-microplastic-ingredients" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          worst microplastic ingredients
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Ingredient list parsed from the product label or barcode.</>,
          <>Flagged ingredients, polyethylene, acrylates, carbomer, PEG, fragrance, parabens.</>,
          <>Product category, leave-on vs rinse-off; risk weighted differently.</>,
          <>Brand and product line, clean certifications (EWG VERIFIED, MADE SAFE).</>,
          <>Cited research and regulatory references for each scan.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan personal-care products before buying"
        sub="The MicroPlastics app reads the ingredient list, flags microplastic polymers and additives, and points to cleaner alternatives in the same category."
        buttonLabel="Scan cosmetics in the app"
      />

      <p>
        For specific brands, we read the ingredient lists that four cult labels
        publish themselves:{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          81 formulas from Fenty, Huda Beauty, rhode and Kylie Cosmetics audited
        </Link>
        . Fenty&apos;s Brown&apos;d Pout lists PET glitter and{' '}
        <Link href="/blog/does-rhode-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          rhode&apos;s Peptide Lip Shape lists polyethylene
        </Link>
        , while the{' '}
        <Link href="/blog/does-kylie-cosmetics-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          Kylie Matte Liquid Lipstick comes out clean
        </Link>
        .
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Does lipstick contain microplastics?',
            answer:
              'Many lipsticks contain polyethylene, PET (especially glitter / shimmer formulas), acrylates copolymer, nylon-12, and other synthetic polymers as binders, film-formers, and texture agents. The Beat the Microbead project found microplastics in dozens of major lipstick brands worldwide.',
          },
          {
            question: 'How much lipstick do you swallow?',
            answer:
              'Average estimates: 24 mg/day for daily wearers, ~8.7 g/year, accumulating to several pounds over a lifetime. Reapplication frequency, eating and drinking, and licking lips all contribute.',
          },
          {
            question: 'What is the safest lipstick brand?',
            answer:
              'Ilia, RMS Beauty, Kosas, Axiology, Lily Lolo, and Hurraw (lip balm) are among the cleanest mainstream brands, most are polymer-free, vegan, and EWG-verified or certified natural. Conventional drugstore lipsticks and long-wear formulations typically contain multiple synthetic polymers.',
          },
          {
            question: 'Is long-wear lipstick worse for microplastics?',
            answer:
              'Yes: "long-wear", "24-hour", "transfer-proof" lipsticks rely heavily on synthetic polymer film-formers (acrylates copolymer, polyquaternium, polyethylene). Standard non-transfer-proof lipsticks are easier to reformulate without polymers.',
          },
          {
            question: 'Is lip balm safer than lipstick?',
            answer:
              'Often yes, since lip balms have simpler formulations. Hurraw, Eco Lips, Burt\'s Bees (Original), Cocokind, and Henne are evidence-supported plant-based lip balm choices. Avoid lip balms with parabens, PEG, polyethylene, or "fragrance".',
          },
          {
            question: 'Are lipsticks really tested for microplastics?',
            answer:
              'Lipsticks have been independently tested by Beat the Microbead, NoMoreMicroplastics, EWG, and various consumer groups. The Plastic Soup Foundation\'s Beat the Microbead app scans products for known microplastic ingredients via a public database.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Beat the Microbead / Plastic Soup Foundation',
            year: 2024,
            title: 'Lipstick full of microplastics: testing project',
            publisher: 'Plastic Soup Foundation',
            url: 'https://www.beatthemicrobead.org/project/lipstick/',
          },
          {
            authors: 'Hepp NM, Mindak WR, Cheng J',
            year: 2009,
            title: 'Determination of total lead in lipstick: development and validation of a microwave-assisted digestion, inductively coupled plasma-mass spectrometric method',
            publisher: 'Journal of Cosmetic Science',
            url: 'https://pubmed.ncbi.nlm.nih.gov/19587895/',
          },
          {
            authors: 'US Food and Drug Administration',
            year: 2022,
            title: 'Lead in Cosmetic Lip Products and Externally Applied Cosmetics',
            publisher: 'FDA',
            url: 'https://www.fda.gov/cosmetics/potential-contaminants-cosmetics/lead-cosmetic-lip-products-and-externally-applied-cosmetics-recommended-maximum-level',
          },
          {
            authors: 'European Chemicals Agency (ECHA)',
            year: 2023,
            title: 'Restriction on intentionally added microplastics',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/hot-topics/microplastics',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
